import React, { useState } from "react";
import axios from "axios";
import Button from "@mui/material/Button";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { toast } from "react-toastify";

import { API_get_report_csv } from "../../../../constants";
import { getDefaultAxiosConfig } from "../../../../utilities/requests";

const BUTTON_SX = {
  borderColor: "#6d28d9",
  color: "#6d28d9",
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": { borderColor: "#5b21b6", backgroundColor: "#f5f3ff" },
};

// The filename the server chose, from Content-Disposition.
export function filenameFromDisposition(disposition, fallback) {
  const match = /filename="?([^";]+)"?/i.exec(disposition || "");
  return match ? match[1] : fallback;
}

// Download CSV for a Report Library report, built on the server from the
// same data as the page (store_info/report_csv), so it matches the table
// and, later, the emailed CSV. start/end are YYYY-MM-DD; extraParams carries
// the page's own filters (e.g. Log Entries' types and search).
export default function ReportCsvDownloadButton({
  reportId,
  storeId,
  start,
  end,
  extraParams,
  disabled = false,
}) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(API_get_report_csv, {
        ...getDefaultAxiosConfig(),
        // A blob keeps the server's bytes (including the UTF-8 BOM Excel
        // needs for characters like ★) instead of re-encoding text.
        responseType: "blob",
        params: { report_id: reportId, store_id: storeId, start, end, ...(extraParams || {}) },
      });
      const filename = filenameFromDisposition(
        res.headers?.["content-disposition"],
        `${reportId}_${start}.csv`
      );
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      // Errors come back as a blob too; show the server's reason when there
      // is one (e.g. a period the report doesn't cover).
      let message = "Couldn't download the CSV. Try again.";
      try {
        const body = JSON.parse(await e?.response?.data?.text());
        if (body?.system?.message) message = body.system.message;
      } catch (_) {
        // Keep the generic message.
      }
      toast.error(message, { autoClose: 4000 });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={<FileDownloadOutlinedIcon />}
      onClick={handleClick}
      disabled={disabled || downloading || !storeId || !start || !end}
      className="prebuilt-report-no-print"
      sx={BUTTON_SX}
    >
      {downloading ? "Preparing CSV…" : "Download CSV"}
    </Button>
  );
}
